import { useParams } from "react-router-dom";
import InfoChip from "../components/common/InfoChip";
import { FeeTypeText, OrderStatus, OrderType, Severity } from "../types";
import { useFetchOrderDetailsQuery } from "../store";
import { OrderStatusLabels, OrderTypeLabels } from "../configs";
import {
  FaUser,
  FaStore,
  FaMotorcycle,
  FaMapMarkerAlt,
  FaReceipt,
  FaBoxOpen,
  FaInfoCircle,
  FaCalendarAlt,
} from "react-icons/fa";

const OrderDetailsWrapper = () => {
  const { orderId } = useParams();

  if (!orderId)
    return (
      <InfoChip
        severity={Severity.DANGER}
        content="Commande introuvable"
      />
    );

  return <OrderDetails orderId={orderId} />;
};

interface OrderDetailsProps {
  orderId: string;
}

const OrderDetails: React.FC<OrderDetailsProps> = ({ orderId }) => {
  const { data: orderDetails, isFetching } = useFetchOrderDetailsQuery(orderId);

  if (isFetching)
    return (
      <div className="p-8 text-center text-gray-500">
        Chargement des détails de la commande...
      </div>
    );

  if (!orderDetails)
    return (
      <InfoChip
        severity={Severity.DANGER}
        content="Commande introuvable"
      />
    );

  const {
    displayId,
    deliveryAddress,
    status,
    orderType,
    city,
    restaurant,
    cart,
    paymentInfo,
    createdAt,
    updatedAt,
    rider,
    user,
  } = orderDetails;

  // --- Tailwind Classes ---
  const cardClass =
    "bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden h-fit";
  const headerClass =
    "flex items-center gap-2 text-lg font-semibold text-gray-800 border-b border-gray-100 p-4 bg-gray-50";
  const contentClass = "p-4 space-y-3";
  const labelClass = "text-sm text-gray-500 font-medium";
  const valueClass = "text-gray-900 font-medium";

  // --- Helper for Badge ---
  const getStatusColor = (s: string) => {
    // Basic mapping, can be expanded based on Severity type if needed
    switch (s) {
      case OrderStatus.COMPLETED:
      case OrderStatus.COLLECTED:
        return "bg-green-100 text-green-800 border-green-200";
      case OrderStatus.CANCELLED:
        return "bg-red-100 text-red-800 border-red-200";
      case OrderStatus.PROCESSING:
      case OrderStatus.COOKING:
      case OrderStatus.WAITING_FOR_RIDER:
      case OrderStatus.WAITING_FOR_PICKUP:
      case OrderStatus.DRIVER_ASSIGNED:
        return "bg-blue-100 text-blue-800 border-blue-200";
      case OrderStatus.WAITING_FOR_PAYMENT:
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  // --- Main Column Sections ---

  // 1. Header / Status
  const renderStatusHeader = (
    <div className={`${cardClass} mb-6`}>
      <div className="p-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            Commande #{displayId}
          </h1>
          <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <FaCalendarAlt /> {createdAt}
            </span>
            {orderType && (
              <span className="px-2 py-0.5 rounded bg-gray-100 border border-gray-200 text-xs font-semibold uppercase">
                {OrderTypeLabels[orderType as OrderType]}
              </span>
            )}
          </div>
        </div>
        <div>
          <span
            className={`px-4 py-2 rounded-full border font-bold text-sm shadow-sm ${getStatusColor(status)}`}
          >
            {OrderStatusLabels[status as OrderStatus]}
          </span>
        </div>
      </div>
    </div>
  );

  // 2. Cart (Order Items)
  const renderCart = (
    <div className={cardClass}>
      <div className={headerClass}>
        <FaBoxOpen className="text-primary" />
        <span>Détail de la commande</span>
      </div>
      <div className="p-0">
        <table className="w-full text-left text-sm">
          <thead className="bg-gray-50 text-gray-500 uppercase font-semibold text-xs border-b">
            <tr>
              <th className="px-4 py-3">Article</th>
              <th className="px-4 py-3 text-right">Prix Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {cart.map((item, idx) => (
              <tr
                key={item.itemId + idx}
                className="hover:bg-gray-50/50"
              >
                <td className="px-4 py-3 align-top">
                  <div className="flex gap-3">
                    <div className="flex items-center justify-center w-8 h-8 rounded bg-gray-100 text-gray-600 font-bold shrink-0">
                      {item.quantity}x
                    </div>
                    <div>
                      <p className="font-bold text-gray-800 text-base">
                        {item.title}
                      </p>
                      {/* Modifiers */}
                      {item.modifierGroups.length > 0 && (
                        <div className="mt-1 space-y-1 text-gray-500 text-xs">
                          {item.modifierGroups.map((group) => (
                            <div key={group.modifierGroupId}>
                              <span className="font-semibold text-gray-600">
                                {group.title}:{" "}
                              </span>
                              {group.modifierOptions.map((opt, i) => (
                                <span key={opt.modifierOptionId}>
                                  {opt.title}{" "}
                                  {opt.quantity > 1 ? `(${opt.quantity})` : ""}
                                  {i < group.modifierOptions.length - 1
                                    ? ", "
                                    : ""}
                                </span>
                              ))}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-right font-semibold text-gray-700 whitespace-nowrap align-top">
                  {item.price.totalPrice.text}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // 3. Payment
  const renderPayment = (
    <div className={cardClass}>
      <div className={headerClass}>
        <FaReceipt className="text-primary" />
        <span>Résumé du paiement</span>
      </div>
      <div className="p-4 space-y-2 text-sm">
        <div className="flex justify-between text-gray-600">
          <span>Sous-total</span>
          <span>{paymentInfo.orderPrice.text}</span>
        </div>

        {paymentInfo.fees.map((fee, index) => (
          <div
            key={index}
            className="flex justify-between text-gray-600"
          >
            <span>{FeeTypeText[fee.type]}</span>
            <span>{fee.value.text}</span>
          </div>
        ))}

        <div className="border-t border-dashed my-2 pt-2 flex justify-between items-center text-lg font-bold text-gray-900">
          <span>Total</span>
          <span>{paymentInfo.totalPrice.text}</span>
        </div>
      </div>
    </div>
  );

  // --- Sidebar Column Sections ---

  // 1. Customer
  const renderCustomer = (
    <div className={cardClass}>
      <div className={headerClass}>
        <FaUser className="text-gray-400" />
        <span>Client</span>
      </div>
      <div className={contentClass}>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-500 font-bold text-lg">
            {user.firstName[0]}
          </div>
          <div>
            <p className={valueClass}>
              {user.firstName} {user.lastName}
            </p>
            <p className="text-xs text-gray-400">ID: {user._id?.slice(-6)}</p>
          </div>
        </div>
        <div className="space-y-1 mt-3 pt-3 border-t border-gray-100">
          <div>
            <p className={labelClass}>Email</p>
            <p
              className="text-sm truncate"
              title={user.email}
            >
              {user.email}
            </p>
          </div>
          <div>
            <p className={labelClass}>Téléphone</p>
            <p className="text-sm">{user.phone}</p>
          </div>
        </div>
      </div>
    </div>
  );

  // 2. Restaurant
  const renderRestaurant = (
    <div className={cardClass}>
      <div className={headerClass}>
        <FaStore className="text-gray-400" />
        <span>Restaurant</span>
      </div>
      <div className={contentClass}>
        <p className={`${valueClass} text-lg`}>{restaurant?.displayName}</p>
        <p className="text-sm text-gray-500 mb-3">{restaurant?.address}</p>

        <div className="border-t border-gray-100 pt-3">
          <p className={`${labelClass} mb-1`}>Contact Référent</p>
          <p className="text-sm font-medium">
            {restaurant?.referant?.firstName} {restaurant?.referant?.lastName}
          </p>
          <p className="text-sm text-gray-600">{restaurant?.referant?.phone}</p>
        </div>
      </div>
    </div>
  );

  // 3. Delivery / Rider
  const renderDelivery = (
    <div className={cardClass}>
      <div className={headerClass}>
        <FaMotorcycle className="text-gray-400" />
        <span>Livraison</span>
      </div>
      <div className={contentClass}>
        <div className="mb-4">
          <div className="flex items-start gap-2 mb-1">
            <FaMapMarkerAlt className="text-red-500 mt-1 shrink-0" />
            <div>
              <p className={labelClass}>Adresse de livraison</p>
              <p className="text-sm text-gray-800 leading-snug">
                {deliveryAddress}
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                {city?.displayName}
              </p>
            </div>
          </div>
        </div>

        {rider ? (
          <div className="bg-gray-50 rounded p-3 border border-gray-100">
            <p className={`${labelClass} mb-2`}>Livreur assigné</p>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 text-xs font-bold">
                {rider.firstName[0]}
                {rider.lastName[0]}
              </div>
              <div>
                <p className="text-sm font-bold text-gray-800">
                  {rider.firstName} {rider.lastName}
                </p>
                <p className="text-xs text-gray-500">
                  {rider.transportType || "Transport inconnu"}
                </p>
              </div>
            </div>
            <div className="mt-2 space-y-0.5">
              <p className="text-xs text-gray-600">
                <span className="font-semibold">Tel:</span> {rider.phone}
              </p>
              <p className="text-xs text-gray-600">
                <span className="font-semibold">Email:</span> {rider.email}
              </p>
            </div>
          </div>
        ) : (
          <div className="bg-yellow-50 rounded p-3 border border-yellow-100 flex gap-2 items-center text-yellow-700">
            <FaInfoCircle />
            <span className="text-sm font-medium">Aucun livreur assigné</span>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="w-full max-w-7xl mx-auto p-6">
      {renderStatusHeader}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content (Left) */}
        <div className="lg:col-span-2 space-y-6">
          {renderCart}
          {renderPayment}
        </div>

        {/* Sidebar (Right) */}
        <div className="lg:col-span-1 space-y-6">
          {renderCustomer}
          {renderDelivery}
          {renderRestaurant}
        </div>
      </div>
    </div>
  );
};

export default OrderDetailsWrapper;
